import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ReactNode, RefObject, useCallback } from 'react';
import ReactDOM from 'react-dom/client';

type queryElementOrRef = string | JSX.Element | RefObject<HTMLElement>;
type pdfOptions = {
  pdfWidth?: number;
  pdfHeight?: number;
  imageWidth?: number;
  imageHeight?: number;
  imageX?: number;
  imageY?: number;
  quality?: 1;
  orientation?: 'p' | 'portrait' | 'l' | 'landscape';
  scale?: number;
};

export default function useHTMLShare(): {
  print: (element: queryElementOrRef) => Promise<void>;
  pdf: (
    elementOrQuerySelector: queryElementOrRef,
    fileName: string,
    options?: ((canvas: HTMLCanvasElement) => pdfOptions) | undefined,
    scale?: number
  ) => Promise<void>;
} {
  const getHTMLElement = useCallback(
    (
      elementOrQuerySelector: queryElementOrRef
    ): Promise<[HTMLElement, boolean]> => {
      if (typeof elementOrQuerySelector === 'string') {
        return Promise.resolve([
          document.querySelector(elementOrQuerySelector) as HTMLElement,
          false,
        ]);
      } else if (
        'current' in elementOrQuerySelector &&
        elementOrQuerySelector.current
      ) {
        return Promise.resolve([elementOrQuerySelector.current, false]);
      } else {
        const el = document.createElement('div');
        el.style.position = 'fixed';
        el.style.top = '150vh';
        el.style.left = '150vw';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        return new Promise<[HTMLElement, boolean]>((resolve) => {
          ReactDOM.createRoot(el).render(elementOrQuerySelector as ReactNode);
          setTimeout(() => {
            resolve([el, true]);
          }, 1000);
        });
      }
    },
    []
  );

  const savePDF = useCallback(
    (
      element: HTMLElement,
      fileName: string,
      options: (canvas: HTMLCanvasElement) => pdfOptions = () => {
        return {};
      },
      scale = 1.5
    ) =>
      new Promise<void>((resolve, reject) => {
        html2canvas(element, { scale })
          .then((canvas) => {
            const opt: pdfOptions = {
              pdfWidth: canvas.width,
              pdfHeight: canvas.height,
              imageWidth: canvas.width,
              imageHeight: canvas.height,
              imageX: 0,
              imageY: 0,
              quality: 1.0,
              orientation: 'p',
              ...(typeof options === 'function' ? options(canvas) : {}),
            };
            const doc = new jsPDF(opt.orientation, 'px', [
              opt.pdfWidth ?? 0,
              opt.pdfHeight ?? 0,
            ]);

            const png = canvas.toDataURL('image/png', opt.quality);
            doc.internal.scaleFactor = scale;
            doc.addImage(
              png,
              'png',
              opt.imageX ?? 0,
              opt.imageY ?? 0,
              opt.imageWidth ?? 0,
              opt.imageHeight ?? 0,
              undefined,
              'NONE'
            );
            doc.save(fileName);
            resolve();
          })
          .catch(() => {
            reject();
          });
      }),
    []
  );

  const pdf = useCallback(
    async (
      elementOrQuerySelector: queryElementOrRef,
      fileName: string,
      options?: (canvas: HTMLCanvasElement) => pdfOptions
    ) => {
      if (!fileName.trim().toLowerCase().endsWith('.pdf')) {
        fileName = `${fileName.trim().toLowerCase()}.pdf`;
      }
      const [e, should] = await getHTMLElement(elementOrQuerySelector);
      if (should) {
        document.body.appendChild(e);
      }
      return savePDF(e, fileName, options).finally(() => {
        if (should) {
          document.body.removeChild(e);
        }
      });
    },
    [getHTMLElement, savePDF]
  );

  const print = useCallback(
    (elementOrQuerySelector: queryElementOrRef) =>
      new Promise<void>((resolve) => {
        getHTMLElement(elementOrQuerySelector).then(([el, should]) => {
          if (should) {
            document.body.appendChild(el);
          }
          html2canvas(el)
            .then((canvas) => {
              const frame = document.createElement('iframe');
              frame.title = '-';
              frame.style.position = 'fixed';
              frame.style.left = '150vw';
              frame.style.top = '150vw';
              document.body.appendChild(frame);
              if (frame.contentDocument && frame.contentWindow) {
                frame.contentDocument.body.style.display = 'flex';
                frame.contentDocument.body.style.alignItems = 'center';
                frame.contentDocument.body.style.justifyContent = 'center';
                frame.contentDocument.body.style.backgroundColor = '#FFFFFF';
                frame.contentDocument?.body.appendChild(canvas);
                frame.focus();
                frame.contentWindow?.print();
              }
              setTimeout(() => {
                document.body.removeChild(frame);
              }, 3000);
              resolve();
            })
            .finally(() => {
              if (should) {
                document.body.removeChild(el);
              }
            });
        });
      }),
    [getHTMLElement]
  );

  return { pdf, print };
}
